/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import {
    string, func, object, bool, shape,
} from 'prop-types';
import { Button, TextField, Grid } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import customBreakpoints from '../../../../../../helpers/customBreakpoints';
import ModifyGiftMessageMultiTextBox from './ModifyGiftMessageMultiTextBox';

const styles = (theme) => ({
    giftMessageRoot: {
        maxHeight: '400px',
        minHeight: '227px',
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            height: '400px',
        },
    },
    multiLineGM: {
        textAlign: 'center',
    },
    multiLineMsgDesktopSize: {
        width: '51%',
    },
    multiLineMsgMobilesize: {
        width: '100%',
    },
    multiLineDesktopTitle: {
        width: '51%',
        display: 'inline-flex',
    },
    multiLineMobileTitl: {
        width: '100%',
        textAlign: 'left',
    },
    copyTitle: {
        margin: '0px',
        padding: '10px 0px'
    },
    nextButton: {
        backgroundColor: theme.palette.colorPrimaryAccountCtaButton,
        color: theme.palette.common.white || '#fff',
        textTransform: 'capitalize',
        fontWeight: 'bold',
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            borderRadius: '20px',
        },
        '&:hover': {
            backgroundColor: theme.palette.colorPrimaryAccountCtaButton,
            opacity: .90,
        },
    },
    backButton: {
        textTransform: 'capitalize',
        fontWeight: 'bold',
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            borderRadius: '20px',
        },
    },
    buttonWrap: {
        position: 'sticky',
        bottom: 0,
        backgroundColor: theme.palette.common.white || '#fff',
    },
    fromFieldContainer: {
        marginBottom: '10px',
    },
    fromText: {
        marginTop: '5px',
        marginBottom: '10px',
    },
});

const ModifyGiftMessage = ({
    giftMessage,
    checkForConfirmationScreen,
    getModifiedGiftMsg,
    classes,
    changeTabs,
    authorName,
    isMobile,
    productBrand,
    modifyEligibility = {},
    featureFlags,
}) => {
    const [giftMsg, setGiftMessage] = useState(giftMessage.replace(/\s+/g, ' ').trim()); // giftMessage is Sender did not request a card message
    const [from, setFrom] = useState(authorName);
    const isCheckoutGiftMessageForm = featureFlags?.['is-using-checkout-gift-message-form'] || false;
    const isCCOMMOGiftMessageEnabled = featureFlags?.['is-cco-mmo-gift-message-enabled'];
    const isRaisedBrand = ['1018', '1019', '1020', '1031'].includes(productBrand); // STY, HD, WLF
    const isCCOBrandEnabled = isCCOMMOGiftMessageEnabled && productBrand === '1012'; // CCO
    const isMultiLineGMEnabled = (isCheckoutGiftMessageForm && (isRaisedBrand || isCCOBrandEnabled)) || false;
    const { isRecipientModifiable, isDeliveryDateModifiable } = modifyEligibility;
    const hdGiftMessage = giftMsg?.includes('\n') ? giftMsg.replaceAll('\n', '') : giftMsg;
    const classTextBoxSize = isMobile ? classes.multiLineMsgMobilesize : classes.multiLineMsgDesktopSize;
    const classTitle = isMobile ? classes.multiLineMobileTitl : classes.multiLineDesktopTitle;

    useEffect(() => () => {
        getModifiedGiftMsg(giftMsg, from);
    }, [giftMsg, from]);

    const gotoConfirmationScreen = () => {
        checkForConfirmationScreen(true);
        getModifiedGiftMsg(giftMsg, from);
    };

    const getMessage = (event) => {
        setGiftMessage(event.target.value);
        // if (event.target.value === '') { // revert this part when we implement feature to allow remove message using mmo
        //     setFrom('');
        // }
    };
    const getSenderName = (event) => {
        setFrom(event.target.value);
    };

    const getMaxLength = () => {
        let maxLength = 180;
        if (isRaisedBrand) maxLength = 115; // HD brands char count max limit is 115, 5 short from 120 to account for 'FROM:'
        else if (isCCOBrandEnabled) maxLength = 150;
        return maxLength;
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} className={isMultiLineGMEnabled ? `${classes.giftMessageRoot} ${classes.multiLineGM}` : classes.giftMessageRoot}>
                <Grid>
                    <p className={isMultiLineGMEnabled ? `${classTitle} ${classes.copyTitle}` : classes.copyTitle}>Enter a message and your name:</p>
                    {isMultiLineGMEnabled
                        ? (
                            <ModifyGiftMessageMultiTextBox
                                setGiftMessage={setGiftMessage}
                                giftMsg={giftMsg}
                                isMobile={isMobile}
                                isCCOBrandEnabled={isCCOBrandEnabled}
                            />
                        )
                        : (
                            <TextField
                                variant="outlined"
                                onChange={getMessage}
                                value={giftMsg}
                                multiline
                                rows={isMobile ? 9 : 4}
                                fullWidth
                                inputProps={{ maxLength: (getMaxLength() - from?.length) }}
                            >
                                {giftMessage}
                            </TextField>
                        )}
                </Grid>
                <Grid className={classes.fromFieldContainer}>
                    <p className={isMultiLineGMEnabled ? `${classTitle} ${classes.fromText}` : classes.fromText}>From</p>
                    <TextField
                        variant="outlined"
                        rows={1}
                        size="small"
                        fullWidth={!isMultiLineGMEnabled}
                        value={from}
                        onChange={getSenderName}
                        inputProps={isMultiLineGMEnabled ? (isRaisedBrand ? { maxLength: 25 } : { maxLength: 30 }) : { maxLength: (getMaxLength() - hdGiftMessage?.length) }}
                        helperText={`${(hdGiftMessage?.length) + (from?.length)}/${getMaxLength()}`}
                        disabled={hdGiftMessage?.length === 0}
                        placeholder={hdGiftMessage?.length === 0 ? 'Add gift message to add author' : ''}
                        className={isMultiLineGMEnabled ? classTextBoxSize : ''}
                    />
                </Grid>
            </Grid>
            <Grid container xs={12} spacing={2} className={classes.buttonWrap} alignItems="center">
                <Grid item xs={6}>
                    <Button
                        className={classes.backButton}
                        fullWidth
                        variant="outlined"
                        disabled={isRecipientModifiable === false && isDeliveryDateModifiable === false}
                        onClick={() => {
                            if (isDeliveryDateModifiable === true) {
                                changeTabs(1);
                            } else {
                                changeTabs(0);
                            }
                        }}
                    >Go Back
                    </Button>
                </Grid>
                <Grid item xs={6}>
                    <Button className={classes.nextButton} fullWidth variant="contained" color="primary" onClick={gotoConfirmationScreen}>Next</Button>
                </Grid>
            </Grid>
        </Grid>
    );
};

ModifyGiftMessage.propTypes = {
    giftMessage: string.isRequired,
    checkForConfirmationScreen: func.isRequired,
    getModifiedGiftMsg: func.isRequired,
    classes: object.isRequired,
    changeTabs: func.isRequired,
    authorName: string.isRequired,
    isMobile: bool.isRequired,
    productBrand: string.isRequired,
    modifyEligibility: shape({
        isRecipientModifiable: bool,
        isDeliveryDateModifiable: bool,
        isCardMsgModifiable: bool,
    }).isRequired,
    featureFlags: shape({
        'is-using-checkout-gift-message-form': bool.isRequired,
    }).isRequired,
};

export default withStyles(styles)(ModifyGiftMessage);
